import { useState, useEffect } from "react";
import { Plus, Trash2, Clock, CalendarOff, Globe, Calendar } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { getTimezoneAbbr } from "@/utils/timezone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const TutorAvailability = () => {
  const [slots, setSlots] = useState([]);
  const [timeOff, setTimeOff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'slot' or 'timeoff'

  // New slot form
  const [newDay, setNewDay] = useState(0);
  const [newStart, setNewStart] = useState("09:00");
  const [newEnd, setNewEnd] = useState("12:00");

  // Time-off form — "single" or "range" mode
  const [offMode, setOffMode] = useState("single");
  const [newOffDate, setNewOffDate] = useState("");
  const [newOffRangeStart, setNewOffRangeStart] = useState("");
  const [newOffRangeEnd, setNewOffRangeEnd] = useState("");
  const [newOffReason, setNewOffReason] = useState("");
  const [addingOff, setAddingOff] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };
      const [slotsRes, offRes] = await Promise.all([
        axios.get(`${API}/tutor/availability`, { headers }),
        axios.get(`${API}/tutor/time-off`, { headers })
      ]);
      setSlots(slotsRes.data);
      setTimeOff(offRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const addSlot = async () => {
    if (newStart >= newEnd) {
      toast.error("End time must be after start time");
      return;
    }
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(`${API}/tutor/availability`, {
        day_of_week: newDay,
        start_time: newStart,
        end_time: newEnd
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Availability added!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add");
    }
  };

  const addTimeOff = async () => {
    setAddingOff(true);
    try {
      const token = localStorage.getItem("access_token");
      if (offMode === "single") {
        if (!newOffDate) { toast.error("Please select a date"); return; }
        await axios.post(`${API}/tutor/time-off`, {
          date: newOffDate,
          reason: newOffReason || null
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Day off added!");
        setNewOffDate("");
      } else {
        if (!newOffRangeStart || !newOffRangeEnd) { toast.error("Please select both a start and end date"); return; }
        if (newOffRangeEnd < newOffRangeStart) { toast.error("End date must be on or after start date"); return; }
        const res = await axios.post(`${API}/tutor/time-off/range`, {
          start_date: newOffRangeStart,
          end_date: newOffRangeEnd,
          reason: newOffReason || null
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success(res.data.message);
        setNewOffRangeStart("");
        setNewOffRangeEnd("");
      }
      setNewOffReason("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add time off");
    } finally {
      setAddingOff(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("access_token");
      const url = deleteType === 'slot'
        ? `${API}/tutor/availability/${deleteTarget}`
        : `${API}/tutor/time-off/${deleteTarget}`;
      await axios.delete(url, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Removed!");
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  // Group consecutive time-off dates into ranges for cleaner display
  const groupTimeOffRanges = (entries) => {
    if (!entries.length) return [];
    const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
    const groups = [];
    let group = { dates: [sorted[0]], reason: sorted[0].reason };

    for (let i = 1; i < sorted.length; i++) {
      const prev = new Date(sorted[i - 1].date + "T12:00:00");
      const curr = new Date(sorted[i].date + "T12:00:00");
      const dayDiff = (curr - prev) / (1000 * 60 * 60 * 24);
      if (dayDiff === 1 && sorted[i].reason === sorted[i - 1].reason) {
        group.dates.push(sorted[i]);
      } else {
        groups.push(group);
        group = { dates: [sorted[i]], reason: sorted[i].reason };
      }
    }
    groups.push(group);
    return groups;
  };

  const formatDateDisplay = (d) =>
    new Date(d + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

  // Group slots by day
  const slotsByDay = {};
  slots.forEach(s => {
    if (!slotsByDay[s.day_of_week]) slotsByDay[s.day_of_week] = [];
    slotsByDay[s.day_of_week].push(s);
  });

  const formatTime = (t) => {
    const [h, m] = t.split(":");
    const hour = parseInt(h);
    return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
  };

  // Count days in the selected range (for range mode preview)
  const rangeDayCount = () => {
    if (!newOffRangeStart || !newOffRangeEnd) return 0;
    const s = new Date(newOffRangeStart + "T12:00:00");
    const e = new Date(newOffRangeEnd + "T12:00:00");
    return Math.max(0, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner"></div></div>;

  const timeOffGroups = groupTimeOffRanges(timeOff);

  return (
    <div>
      <h1 className="text-3xl font-bold mb-2" data-testid="availability-title">My Availability</h1>
      <p className="text-[#4A4A4A] mb-2">Set your weekly recurring schedule and days off</p>
      <p className="text-xs text-[#3C8DE4] font-medium flex items-center gap-1 mb-8" data-testid="tutor-timezone-indicator">
        <Globe className="w-3 h-3" /> Times are in your local timezone ({getTimezoneAbbr()})
      </p>

      {/* Weekly Schedule */}
      <div className="brutalist-card p-6 mb-8" data-testid="weekly-schedule">
        <h2 className="font-bold text-xl mb-4 flex items-center gap-2"><Clock className="w-5 h-5" /> Weekly Schedule</h2>

        {/* Add new slot */}
        <div className="flex items-end gap-3 mb-6 flex-wrap bg-[#F0EFEB] p-4 rounded-lg">
          <div>
            <label className="text-xs font-bold uppercase block mb-1">Day</label>
            <select value={newDay} onChange={e => setNewDay(parseInt(e.target.value))} className="brutalist-input text-sm" data-testid="avail-day-select">
              {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase block mb-1">From</label>
            <input type="time" value={newStart} onChange={e => setNewStart(e.target.value)} className="brutalist-input text-sm" data-testid="avail-start-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase block mb-1">To</label>
            <input type="time" value={newEnd} onChange={e => setNewEnd(e.target.value)} className="brutalist-input text-sm" data-testid="avail-end-input" />
          </div>
          <button onClick={addSlot} className="brutalist-btn-primary text-sm flex items-center gap-1" data-testid="add-slot-btn">
            <Plus className="w-4 h-4" /> Add Slot
          </button>
        </div>

        {/* Display weekly grid */}
        <div className="space-y-3">
          {DAY_NAMES.map((dayName, dayIndex) => (
            <div key={dayIndex} className={`flex items-center gap-4 p-3 rounded-lg ${slotsByDay[dayIndex] ? 'bg-[#D6E6FA]/20 border-2 border-[#0A0A0A]' : 'bg-[#F0EFEB]/50'}`}>
              <div className="w-24 font-bold text-sm">{DAY_SHORT[dayIndex]}</div>
              <div className="flex-1 flex gap-2 flex-wrap">
                {slotsByDay[dayIndex] ? slotsByDay[dayIndex].map(slot => (
                  <div key={slot.id} className="flex items-center gap-2 bg-white border-2 border-[#0A0A0A] rounded-full px-3 py-1" data-testid={`slot-${slot.id}`}>
                    <span className="text-sm font-medium">{formatTime(slot.start_time)} - {formatTime(slot.end_time)}</span>
                    <button
                      onClick={() => { setDeleteTarget(slot.id); setDeleteType('slot'); }}
                      className="text-[#FF6B6B] hover:text-red-700"
                      data-testid={`delete-slot-${slot.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )) : (
                  <span className="text-sm text-[#4A4A4A] italic">No availability</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Time Off */}
      <div className="brutalist-card p-6" data-testid="time-off-section">
        <h2 className="font-bold text-xl mb-1 flex items-center gap-2"><CalendarOff className="w-5 h-5" /> Time Off</h2>
        <p className="text-sm text-[#4A4A4A] mb-5">Block specific dates — students won't be able to book sessions that fall on these days.</p>

        {/* Mode toggle */}
        <div className="inline-flex rounded-lg border-2 border-[#0A0A0A] overflow-hidden mb-5" data-testid="timeoff-mode-toggle">
          <button
            onClick={() => setOffMode("single")}
            className={`px-4 py-2 text-sm font-bold transition-colors ${offMode === "single" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F0EFEB]"}`}
            data-testid="mode-single"
          >
            Single Day
          </button>
          <button
            onClick={() => setOffMode("range")}
            className={`px-4 py-2 text-sm font-bold transition-colors ${offMode === "range" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#F0EFEB]"}`}
            data-testid="mode-range"
          >
            Date Range
          </button>
        </div>

        <div className="flex items-end gap-3 mb-6 flex-wrap bg-[#F0EFEB] p-4 rounded-lg">
          {offMode === "single" ? (
            <div>
              <label className="text-xs font-bold uppercase block mb-1">Date</label>
              <input type="date" value={newOffDate} onChange={e => setNewOffDate(e.target.value)} className="brutalist-input text-sm" data-testid="timeoff-date-input" />
            </div>
          ) : (
            <>
              <div>
                <label className="text-xs font-bold uppercase block mb-1">Start Date</label>
                <input type="date" value={newOffRangeStart} onChange={e => setNewOffRangeStart(e.target.value)} className="brutalist-input text-sm" data-testid="timeoff-range-start" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase block mb-1">End Date</label>
                <input type="date" value={newOffRangeEnd} onChange={e => setNewOffRangeEnd(e.target.value)} className="brutalist-input text-sm" data-testid="timeoff-range-end" />
              </div>
              {rangeDayCount() > 0 && (
                <div className="flex items-center gap-1 text-xs font-bold text-[#3C8DE4] self-end pb-2">
                  <Calendar className="w-3 h-3" />
                  {rangeDayCount()} day{rangeDayCount() !== 1 ? "s" : ""}
                </div>
              )}
            </>
          )}
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs font-bold uppercase block mb-1">Reason (optional)</label>
            <input type="text" value={newOffReason} onChange={e => setNewOffReason(e.target.value)} placeholder="e.g., Holiday, Personal" className="brutalist-input text-sm w-full" data-testid="timeoff-reason-input" />
          </div>
          <button onClick={addTimeOff} disabled={addingOff} className="brutalist-btn-primary text-sm flex items-center gap-1 whitespace-nowrap" data-testid="add-timeoff-btn">
            <Plus className="w-4 h-4" /> {addingOff ? "Adding..." : offMode === "range" ? "Block Dates" : "Add Day Off"}
          </button>
        </div>

        {/* Display time off — grouped into ranges */}
        {timeOff.length === 0 ? (
          <p className="text-sm text-[#4A4A4A] italic">No time off scheduled</p>
        ) : (
          <div className="space-y-2">
            {timeOffGroups.map((group, gi) => (
              <div key={gi} className="flex items-center gap-3 p-3 border-2 border-[#0A0A0A] rounded-lg">
                <CalendarOff className="w-4 h-4 text-[#FF6B6B] flex-shrink-0" />
                <div className="flex-1">
                  {group.dates.length === 1 ? (
                    <span className="font-bold">{formatDateDisplay(group.dates[0].date)}</span>
                  ) : (
                    <span className="font-bold">
                      {formatDateDisplay(group.dates[0].date)} → {formatDateDisplay(group.dates[group.dates.length - 1].date)}
                      <span className="ml-2 text-xs font-normal text-[#4A4A4A]">({group.dates.length} days)</span>
                    </span>
                  )}
                  {group.reason && <span className="text-sm text-[#4A4A4A] ml-2">— {group.reason}</span>}
                </div>
                {/* Delete individual dates in the group */}
                <div className="flex gap-1">
                  {group.dates.map(entry => (
                    <button
                      key={entry.id}
                      onClick={() => { setDeleteTarget(entry.id); setDeleteType('timeoff'); }}
                      className={`p-1.5 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded text-xs ${group.dates.length > 1 ? "hidden" : ""}`}
                      data-testid={`delete-timeoff-${entry.id}`}
                      title={`Remove ${entry.date}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  ))}
                  {group.dates.length > 1 && (
                    <button
                      onClick={() => { setDeleteTarget(group.dates.map(d => d.id)); setDeleteType('timeoff-range'); }}
                      className="p-1.5 text-[#FF6B6B] hover:bg-[#FFAEBC] rounded"
                      data-testid={`delete-timeoff-group-${gi}`}
                      title="Remove all dates in this block"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={deleteType === 'slot' ? "Remove Availability Slot?" : "Remove Time Off?"}
        description={deleteType === 'timeoff-range' ? `This will remove all ${deleteTarget?.length} days in this block.` : "This action cannot be undone."}
        onConfirm={async () => {
          if (deleteType === 'timeoff-range' && Array.isArray(deleteTarget)) {
            // Bulk delete
            const token = localStorage.getItem("access_token");
            await Promise.all(deleteTarget.map(id =>
              axios.delete(`${API}/tutor/time-off/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            ));
            toast.success("Time-off block removed!");
            setDeleteTarget(null);
            loadData();
          } else {
            handleDelete();
          }
        }}
        destructive
      />
    </div>
  );
};

export default TutorAvailability;
